<template>
  <div class="p-6">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Audit Log Details</h1>
      <button
        @click="goBack"
        class="btn bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
      >
        Back to List
      </button>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Loading Message -->
    <div v-if="isLoading" class="text-gray-600 mb-4">
      Loading audit log details...
    </div>

    <!-- Audit Log Details -->
    <div
      v-if="!isLoading && !error && log"
      class="glass-card p-6"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 dark:text-gray-50">
        <div class="col-span-2">
          <h2 class="text-xl font-semibold mb-2">Basic Information</h2>
        </div>
        <div>
          <p class="text-sm text-gray-600 dark:text-gray-50">Timestamp</p>
          <p class="font-medium">{{ formatDateTime(log.timestamp) }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-600 dark:text-gray-50">Action</p>
          <p :class="['font-medium', getActionClass(log.action)]">
            {{ getActionLabel(log.action) }}
          </p>
        </div>
        <div>
          <p class="text-sm text-gray-600 dark:text-gray-50">User</p>
          <p class="font-medium">{{ log.user }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-600 dark:text-gray-50">Content Type</p>
          <p class="font-medium">{{ log.content_type }}</p>
        </div>
        <div class="col-span-2">
          <p class="text-sm text-gray-600 dark:text-gray-50">Object Representation</p>
          <p class="font-medium">{{ log.object_repr }}</p>
        </div>
        <div class="col-span-2">
          <h2 class="text-xl font-semibold mb-2 mt-4 dark:text-gray-50">Changes</h2>
          <div class="bg-gray-100 dark:bg-gray-700 p-4 rounded-md overflow-x-auto">
            <div
              v-for="(change, key) in log.changes"
              :key="key"
              class="mb-6 last:mb-0"
            >
              <h3 class="font-semibold text-lg mb-2">
                {{ formatFieldName(key) }}
              </h3>
              <div class="flex items-center">
                <div
                  class="flex-1 bg-white dark:bg-gray-800 p-3 rounded-l-md border-l-4 border-red-500"
                >
                  <p class="text-sm text-gray-600 mb-1 dark:text-gray-50">Previous Value:</p>
                  <p class="font-medium">{{ formatValue(change[0]) }}</p>
                </div>
                <div class="mx-4 text-gray-400 dark:text-gray-50">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </div>
                <div
                  class="flex-1 bg-white dark:bg-gray-800  p-3 rounded-r-md border-r-4 border-green-500"
                >
                  <p class="text-sm text-gray-600 mb-1 dark:text-gray-50">New Value:</p>
                  <p class="font-medium">{{ formatValue(change[1]) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getAuditLogDetails } from "@/services/auditLogService";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "AuditLogDetails",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const tenant = useMultiTenant();
    const log = ref(null);
    const error = ref(null);
    const isLoading = ref(false);

    const fetchLogDetails = async () => {
      isLoading.value = true;
      try {
        const logId = route.params.id;
        const { schemaName } = tenant.validateSchemaForOperation();
        log.value = await getAuditLogDetails(logId, schemaName);
        error.value = null;
      } catch (err) {
        console.error("Error fetching audit log details:", err);
        error.value =
          "Failed to fetch audit log details. Please try again later.";
        log.value = null;
      } finally {
        isLoading.value = false;
      }
    };

    const formatDateTime = (dateString) => {
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    };

    const formatFieldName = (fieldName) => {
      return fieldName
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const formatValue = (value) => {
      if (value === null || value === "None") {
        return "None";
      }
      return value;
    };

    const getActionLabel = (action) => {
      switch (action) {
        case 0:
          return "create";
        case 1:
          return "update";
        case 2:
          return "delete";
        case 3:
          return "access";
        default:
          return "unknown";
      }
    };

    const getActionClass = (action) => {
      switch (action) {
        case 0: // create
          return "text-green-600";
        case 1: // update
          return "text-blue-600";
        case 2: // delete
          return "text-red-600";
        default:
          return "text-gray-600";
      }
    };

    const goBack = () => {
      router.push({ name: "AuditLogs", query: tenant.addSchemaParam() });
    };

    onMounted(async () => {
      await tenant.fetchOrganizations();
      await fetchLogDetails();
    });

    return {
      log,
      error,
      isLoading,
      formatDateTime,
      getActionLabel,
      getActionClass,
      goBack,
      formatFieldName,
      formatValue,
    };
  },
};
</script>
